import { apiGet, apiDelete, apiPatch } from '../../utils/api';
import { config } from '../../config/app-config';
import { getAuthToken, isAuthenticated } from '../auth';
import { showErrorNotification, showSuccessNotification } from '../../utils/notifications';
import { openLinkPageModal } from './link-page-modal';

export class DiagnosticManager {
    constructor() {
        this.init();
    }

    init() {
        document.addEventListener('DOMContentLoaded', () => this.processExistingBlocks());
        document.addEventListener('blockSystemReady', () => this.processExistingBlocks());
        document.addEventListener('blockAdded', this._handleBlockAdded.bind(this));
        document.addEventListener('blockPublished', this._handleBlockPublished.bind(this));
    }

    _handleBlockAdded(event) {
        const { blockElement } = event.detail;
        const blockName = blockElement.getAttribute('data-block-name');
        if (blockName && blockName.startsWith('diagnostic-card-') && isAuthenticated()) {
            this.addLinkButton(blockElement);
        }
    }

    _handleBlockPublished(event) {
        const { blockElement, blockId } = event.detail;
        const blockName = blockElement.getAttribute('data-block-name');
        if (!blockName || !blockName.startsWith('diagnostic-card-')) return;

        const pendingId = blockElement.getAttribute('data-pending-link-id');
        const pendingSlug = blockElement.getAttribute('data-pending-link-slug');
        if (pendingId) {
            this._applyPendingLink(blockElement, blockId, pendingId, pendingSlug);
        }
    }

    async _applyPendingLink(blockElement, blockId, entityId, entitySlug) {
        try {
            const result = await apiPatch(
                `${config.api.paths.diagnosticsLinkBlock}/${entityId}/block`,
                { block_id: parseInt(blockId, 10) }
            );
            if (result?.id) {
                blockElement.setAttribute('data-diagnostic-id', result.id);
                blockElement.setAttribute('data-diagnostic-slug', result.slug || entitySlug);
                blockElement.removeAttribute('data-pending-link-id');
                blockElement.removeAttribute('data-pending-link-slug');
                this._activateLink(blockElement, result.slug || entitySlug);
            }
        } catch (err) {
            console.error('Error applying pending diagnostic link:', err);
        }
    }

    _activateLink(blockElement, slug) {
        const link = blockElement.querySelector('a.diagnostic-card-link');
        if (!link) return;

        if (blockElement._cardClickHandler) {
            blockElement.removeEventListener('click', blockElement._cardClickHandler);
            delete blockElement._cardClickHandler;
        }

        if (slug) {
            const href = `/diagnostic/${slug}`;
            link.href = href;
            link.classList.remove('inactive');
            link.classList.add('active');

            blockElement._cardClickHandler = (e) => {
                if (!e.target.closest('a, button')) {
                    window.location.href = href;
                }
            };
            blockElement.addEventListener('click', blockElement._cardClickHandler);
            blockElement.style.cursor = 'pointer';
        } else {
            link.href = '#';
            link.classList.remove('active');
            link.classList.add('inactive');
            blockElement.style.cursor = '';
        }
    }

    addLinkButton(blockElement) {
        if (!isAuthenticated()) return;
        if (blockElement.querySelector('.diagnostic-link-btn')) return;

        const btn = document.createElement('button');
        btn.className = 'diagnostic-link-btn block-control-btn';
        btn.title = 'Прив\'язати сторінку';
        btn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1 1 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4 4 0 0 1-.128-1.287z"/>
                <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243z"/>
            </svg>
        `;
        Object.assign(btn.style, {
            position: 'absolute',
            top: '4px',
            right: '28px',
            background: 'rgba(0,0,0,0.5)',
            border: 'none',
            borderRadius: '4px',
            color: 'white',
            cursor: 'pointer',
            padding: '4px',
            zIndex: '10',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        });

        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const blockId = blockElement.getAttribute('data-block-id') || null;
            const currentSlug = blockElement.getAttribute('data-diagnostic-slug') || blockElement.getAttribute('data-pending-link-slug') || null;
            openLinkPageModal(blockId, currentSlug, (service) => {
                if (blockId) {
                    blockElement.setAttribute('data-diagnostic-id', service.id);
                    blockElement.setAttribute('data-diagnostic-slug', service.slug);
                    this._activateLink(blockElement, service.slug);
                } else {
                    blockElement.setAttribute('data-pending-link-id', service.id);
                    blockElement.setAttribute('data-pending-link-slug', service.slug);
                    btn.style.background = 'rgba(25,118,210,0.8)';
                }
            });
        });

        blockElement.appendChild(btn);
    }

    processExistingBlocks() {
        document.querySelectorAll('[data-block-name^="diagnostic-card-"]').forEach(el => {
            const slug = el.getAttribute('data-diagnostic-slug');
            if (slug) {
                this._activateLink(el, slug);
            } else {
                const blockId = el.getAttribute('data-block-id');
                if (blockId) this._loadBlockData(el, blockId);
            }

            if (isAuthenticated()) this.addLinkButton(el);
        });
    }

    async _loadBlockData(blockElement, blockId) {
        try {
            const data = await apiGet(`${config.api.paths.diagnosticsByBlockId}/${blockId}`, { auth: false });
            if (data?.slug) {
                blockElement.setAttribute('data-diagnostic-id', data.id);
                blockElement.setAttribute('data-diagnostic-slug', data.slug);
                this._activateLink(blockElement, data.slug);
            }
        } catch {
            // No diagnostic linked yet — link stays inactive
        }
    }

    static async handleBlockDeletion(blockElement) {
        const serviceId = blockElement.getAttribute('data-diagnostic-id');
        if (!serviceId || !getAuthToken()) return false;

        try {
            await apiDelete(`${config.api.paths.diagnostics}/${serviceId}`);
            return true;
        } catch (err) {
            console.error('Error deleting diagnostic service:', err);
            return false;
        }
    }
}

export const diagnosticManager = new DiagnosticManager();
window.diagnosticManager = diagnosticManager;
